import { baseApi, PAYMENTS_TAG } from '@/shared';
import {
  h2hPaymentCountsAdapter,
  h2hPaymentsAdapter,
} from '../utils/adapter';

import type {
  TGetH2HPayments,
  TH2HPaymentCounts,
  TH2HPaymentCountsServer,
  TH2HPaymentFilters,
  TH2HSettlementSource,
  TH2HPaymentsResponse,
  TH2HPaymentsResponseServer,
} from './types';

const getFilterParams = (params: TH2HPaymentFilters) => ({
  id: params.id,
  created_from: params.createdFrom,
  created_to: params.createdTo,
  payer_id: params.payerId,
  shop_id: params.shopId,
  method_id: params.methodId || undefined,
  remote_id: params.remoteId,
  credentials: params.credentials,
});

export const h2hPaymentsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getH2HPayments: build.query<TH2HPaymentsResponse, TGetH2HPayments>({
      query: params => ({
        url: '/admin/h2h-payments/list',
        params: {
          ...getFilterParams(params),
          tab: params.tab,
          page_size: params.pageSize,
          page: params.page,
        },
      }),
      transformResponse: (response: TH2HPaymentsResponseServer) => h2hPaymentsAdapter(response),
      providesTags: [PAYMENTS_TAG],
    }),
    getH2HPaymentCounts: build.query<TH2HPaymentCounts, TH2HPaymentFilters>({
      query: params => ({
        url: '/admin/h2h-payments/counts',
        params: getFilterParams(params),
      }),
      transformResponse: (response: TH2HPaymentCountsServer) => h2hPaymentCountsAdapter(response),
      providesTags: [PAYMENTS_TAG],
    }),
    creditH2HPaymentClient: build.mutation<void, { paymentId: number; paidAmount: string }>({
      query: ({ paymentId, paidAmount }) => ({
        url: `/admin/h2h-payments/${paymentId}/credit-client`,
        method: 'PUT',
        body: { paid_amount: paidAmount },
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),
    rejectH2HPayment: build.mutation<void, { paymentId: number; reason: string }>({
      query: ({ paymentId, reason }) => ({
        url: `/admin/h2h-payments/${paymentId}/reject`,
        method: 'PUT',
        body: { reason },
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),
    finalizeH2HPayment: build.mutation<void, { paymentId: number; settlementSource: TH2HSettlementSource }>({
      query: ({ paymentId, settlementSource }) => ({
        url: `/admin/h2h-payments/${paymentId}/finalize`,
        method: 'PUT',
        body: { settlement_source: settlementSource },
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),
    resolveH2HProviderConflict: build.mutation<void, number>({
      query: paymentId => ({
        url: `/admin/h2h-payments/${paymentId}/resolve-provider-conflict`,
        method: 'PUT',
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),
    reviewH2HDiscrepancy: build.mutation<void, number>({
      query: paymentId => ({
        url: `/admin/h2h-payments/${paymentId}/review-discrepancy`,
        method: 'PUT',
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),
    hideH2HPaymentFromQueue: build.mutation<void, number>({
      query: paymentId => ({
        url: `/admin/h2h-payments/${paymentId}/hide-from-queue`,
        method: 'PUT',
      }),
      invalidatesTags: [PAYMENTS_TAG],
    }),
  }),
});

export const {
  useGetH2HPaymentsQuery,
  useGetH2HPaymentCountsQuery,
  useCreditH2HPaymentClientMutation,
  useRejectH2HPaymentMutation,
  useFinalizeH2HPaymentMutation,
  useResolveH2HProviderConflictMutation,
  useReviewH2HDiscrepancyMutation,
  useHideH2HPaymentFromQueueMutation,
} = h2hPaymentsApi;
